// ArticleEditor.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { buildRichTextSummary, normalizeRichTextValue, serializeRichTextDocument } from 'utilities/RichText';

// Hooks
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';

// CSS
import './ArticleEditor.css';

// Components
import AddModal from 'components/addmodal/AddModal';
import RichTextEditor from 'components/richtext/RichTextEditor';
import CameraModal from 'editor/nodeadd/camera/CameraModal';

const AUTOSAVE_DELAY_MS = 1000;

const toEmbeddedNodeCandidate = (node, baseKey) => {
    if (!node) return null;

    const nodeKey = String(node?.nodeKey || node?.key || '').trim();
    if (!nodeKey) return null;

    return {
        key: nodeKey,
        baseKey: String(node?.baseKey || baseKey || '').trim(),
        type: String(node?.nodeType || node?.type || 'NODE').trim() || 'NODE',
        title: String(node?.title || '').trim(),
        subtitle: String(node?.subtitle ?? node?.description ?? '').trim(),
        coverStoragePath: String(node?.coverStoragePath || '').trim(),
        coverUrl: String(node?.coverUrl || '').trim(),
    };
};

/**
 * ArticleEditor
 *
 * Renders the node-level article editing experience in the highlight pane.
 *
 * @param {boolean} autosize - Whether the highlight pane should autosize.
 * @param {boolean} editable - Whether the current user can edit the article.
 * @param {object} node - Active node record.
 * @param {function} onSave - Persists article updates on the node.
 *
 * @returns {ReactNode} The rendered article editor.
 */
const ArticleEditor = React.memo(({
    autosize = false,
    editable = false,
    node,
    onSave,
}) => {

    // ----- CONTEXT ----- //

    // Language Context

    const language = useCurrentLanguage();

    // Navigation Context
    const { setCurrentNode, setCurrentSection } = useNavigation();

    // Node Types Context
    const { getType } = useNodeTypes();

    // User Context
    const { user } = useUser();

    // Base Context
    const { activeBase } = useBase();

    // ----- ROUTING ----- //

    const navigate = useNavigate();

    // ----- STATE ----- //

    const [draftValue, setDraftValue] = useState(() => normalizeRichTextValue(node?.article));
    const [isDirty, setIsDirty] = useState(false);
    const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
    const [, setSaveStatus] = useState('saved');

    // ----- MEMOS ----- //

    const normalizedValue = useMemo(() => normalizeRichTextValue(node?.article), [node?.article]);
    const normalizedSerializedValue = useMemo(() => serializeRichTextDocument(normalizedValue.content), [normalizedValue.content]);
    const placeholder = useMemo(() => {
        const title = String(node?.title || '').trim();
        return title ? `Write ${title.toLowerCase()}...` : 'Write article...';
    }, [node?.title]);

    // ----- REFS ----- //

    const committedSerializedRef = useRef(normalizedSerializedValue);
    const draftValueRef = useRef(normalizedValue);
    const editorRef = useRef(null);

    draftValueRef.current = draftValue;

    // ----- MEMOS ----- //

    const persistDraft = useCallback(async (nextValue) => {
        if (!editable || !node?.key || typeof onSave !== 'function') return false;

        const normalizedNextValue = normalizeRichTextValue(nextValue);
        const nextSerialized = serializeRichTextDocument(normalizedNextValue.content);

        if (nextSerialized === committedSerializedRef.current) {
            setIsDirty(false);
            setSaveStatus('saved');
            return true;
        }

        setSaveStatus('saving');

        const didSave = await Promise.resolve(onSave(node.key, {
            article: normalizedNextValue,
            articleSummary: buildRichTextSummary(normalizedNextValue),
        }));

        if (didSave === false) {
            setSaveStatus('error');
            return false;
        }

        committedSerializedRef.current = nextSerialized;
        setDraftValue(normalizedNextValue);
        setIsDirty(false);
        setSaveStatus('saved');
        return true;
    }, [editable, node?.key, onSave]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const nextSerialized = normalizedSerializedValue;
        const previousSerialized = committedSerializedRef.current;

        committedSerializedRef.current = nextSerialized;

        if (isDirty && nextSerialized === previousSerialized) return;

        setDraftValue(normalizedValue);
        setIsDirty(false);
        setSaveStatus('saved');
    }, [isDirty, normalizedSerializedValue, normalizedValue]);

    useEffect(() => {
        if (!editable || !isDirty) return undefined;

        setSaveStatus((current) => (current === 'saving' ? current : 'unsaved'));

        const timeoutId = window.setTimeout(() => {
            const latestDraft = draftValueRef.current;
            if (!latestDraft) return;
            void persistDraft(latestDraft);
        }, AUTOSAVE_DELAY_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [editable, isDirty, draftValue, persistDraft]);

    useEffect(() => {
        return () => {
            if (!editable || !isDirty) return;
            const latestDraft = draftValueRef.current;
            if (!latestDraft) return;
            void persistDraft(latestDraft);
        };
    }, [editable, isDirty, persistDraft]);

    // ----- HANDLERS ----- //

    /**
 * Handles editor change.
 *
 * @param {*} nextValue
 */

    const handleEditorChange = useCallback((nextValue) => {
        const normalizedNextValue = normalizeRichTextValue(nextValue);
        const nextSerialized = serializeRichTextDocument(normalizedNextValue.content);

        setDraftValue(normalizedNextValue);
        setIsDirty(nextSerialized !== committedSerializedRef.current);

        if (nextSerialized !== committedSerializedRef.current) {
            setSaveStatus('unsaved');
        }
    }, []);

    /**
 * Handles editor blur.
 *
 * @param {*} nextValue
 */
    const handleEditorBlur = useCallback((nextValue) => {
        const normalizedNextValue = normalizeRichTextValue(nextValue);
        setDraftValue(normalizedNextValue);
        void persistDraft(normalizedNextValue);
    }, [persistDraft]);

    /**
 * Handles insert nodes.
 */
    const handleInsertNodes = useCallback((rows = []) => {
        let insertedAny = false;

        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const candidate = toEmbeddedNodeCandidate(row, activeBase?.key || '');
            if (!candidate) return;
            insertedAny = editorRef.current?.insertEmbeddedNode(candidate) || insertedAny;
        });

        if (insertedAny) {
            setIsInsertModalOpen(false);
            editorRef.current?.focus();
        }
    }, [activeBase?.key]);

    /**
 * Handles open insert modal.
 */
    const handleOpenInsertModal = useCallback(() => {
        if (!editable) return;
        setIsInsertModalOpen(true);
    }, [editable]);

    /**
 * Handles select existing.
 *
 * @param {*} selectedNode
 */
    const handleSelectExisting = useCallback((selectedNode) => {
        handleInsertNodes([selectedNode]);
    }, [handleInsertNodes]);

    // ----- HOOKS ----- //

    const {
        cameraOpen,
        cameraContentType,
        handleCameraContentCreated,
        handleTopLevelActionSelect,
        handleUploadFileSelect,
        pendingUploadAccept,
        pendingUploadMultiple,
        setCameraOpen,
        uploadInputRef,
    } = useTopLevelNodeCreate({
        activeBaseKey: activeBase?.key || null,
        getType,
        language,
        navigate,
        onNodesCreated: handleInsertNodes,
        setCurrentNode,
        setCurrentSection,
        shouldNavigate: false,
        user,
    });

    // ----- HANDLERS ----- //

    const handleSelectType = useCallback((typeKey) => {
        handleTopLevelActionSelect(typeKey, {
            shouldNavigate: false,
        });
    }, [handleTopLevelActionSelect]);

    // ----- RENDER ----- //

    return (
        <div className="article-editor-container">

            {/* SURFACE */}
            <div className="article-editor-surface">
                <RichTextEditor
                  ref={editorRef}
                  disabled={!editable}
                  placeholder={placeholder}
                  toolbarPreset="article"
                  value={draftValue}
                  onBlur={handleEditorBlur}
                  onChange={handleEditorChange}
                  onInsertNodeRequest={handleOpenInsertModal}
                />
            </div>

            <AddModal
              insertMode
              isOpen={isInsertModalOpen}
              node={null}
              onClose={() => setIsInsertModalOpen(false)}
              onExistingSelected={handleSelectExisting}
              onTypeSelect={handleSelectType}
            />

            <input
              style={{
                    display: 'none',
                }}
              ref={uploadInputRef}
              accept={pendingUploadAccept}
              multiple={pendingUploadMultiple}
              type="file"
              onChange={handleUploadFileSelect}
            />

            <CameraModal
              contentType={cameraContentType}
              isOpen={cameraOpen}
              setOpen={setCameraOpen}
              onContentCreated={handleCameraContentCreated}
            />
        </div>
    );
});

// Dev Tools
ArticleEditor.displayName = 'ArticleEditor';

export default ArticleEditor;
